import React, {FC, useState} from "react";
import {PhotoProvider, PhotoSlider} from "react-photo-view";
import {getPhotoTags} from "enums";
import {ModerationUser} from "interfaces";
import {PhotoPayloadBloom} from "services";
import {Button} from "react-bootstrap";
import {usePhotoManagement} from "components/ModerationUserCard/ModerationPhotos/usePhotoManagement";
import {
    ModerationPhotoTags
} from "components/ModerationUserCard/ModerationPhotos/ModerationPhotoTags/ModerationPhotoTags";

interface Props {
    user: ModerationUser
    userId: string
    children: React.ReactNode
    photos: PhotoPayloadBloom[]

    onPhotoDeleted?: (key: string) => void
    onSensitiveTagChanged?: (key: string, isSensitive: boolean) => void

    // Ключ фото, которое нужно показать в большом просмотре (например, из урла)
    openedPhotoKey?: string | null
    // Вызывается с ключом фото, открытого в большом просмотре, или null при закрытии
    onOpenedPhotoChange?: (key: string | null) => void
}

export const ModerationPhotoProvider: FC<Props> =
    ({
         userId,
         user,
         photos,
         children,
         onPhotoDeleted,
         onSensitiveTagChanged,
         openedPhotoKey,
         onOpenedPhotoChange
     }) => {
        const photoManager = usePhotoManagement()
        const [isProviderVisible, setIsProviderVisible] = useState(false)

        // PhotoProvider нельзя открыть программно, поэтому фото по openedPhotoKey показываем в управляемом PhotoSlider.
        // Пока открыт просмотр по клику на миниатюру, слайдер скрыт, чтобы не было двух просмотров
        const openedPhotoIndex = openedPhotoKey ? photos.findIndex(p => p.key === openedPhotoKey) : -1
        const isSliderVisible = !isProviderVisible && openedPhotoIndex >= 0

        const viewerProps = {
            maskOpacity: 0.93,
            speed: () => 200,
            bannerVisible: true,
            overlayRender: (props: { index: number }) => {
                const photo = photos[props.index]
                if (!photo) {
                    return null
                }
                const photoTags = getPhotoTags(user, photo.key)
                return <div className="mx-4 d-flex flex-row" style={{gap: 10, position: 'absolute', bottom: 0}}>
                    <ModerationPhotoTags reviewTags={photoTags} isSensitive={photo.isSensitive} hasNoFace={false}/>
                </div>
            },
            toolbarRender: (props: { index: number }) => {
                return <div className="mx-4 d-flex flex-row" style={{gap: 10}}>
                    {onPhotoDeleted &&
                        <Button variant="danger" onClick={async () => {
                            if (!photos[props.index]) {
                                return
                            }
                            const deletedKey = await photoManager.onPicDelete(photos[props.index], userId)
                            deletedKey && onPhotoDeleted(deletedKey)
                        }}>Удалить фото</Button>}
                    {onSensitiveTagChanged &&
                        <Button variant="secondary" onClick={async () => {
                            const photo = photos[props.index]
                            if (!photo) {
                                return
                            }
                            const isSensitive = await photoManager.toggleSensitive(photo, userId)
                            photo && isSensitive !== undefined && onSensitiveTagChanged(photo.key, isSensitive)
                        }}>{photos[props.index]?.isSensitive ? "Удалить Sensitive" : "Добавить Sensitive"}</Button>}
                </div>
            },
        }

        return <>
            <PhotoProvider
                {...viewerProps}
                onVisibleChange={(visible, index) => {
                    setIsProviderVisible(visible)
                    onOpenedPhotoChange?.(visible ? photos[index]?.key ?? null : null)
                }}
                onIndexChange={index => onOpenedPhotoChange?.(photos[index]?.key ?? null)}>
                {children}
            </PhotoProvider>
            {onOpenedPhotoChange &&
                <PhotoSlider
                    {...viewerProps}
                    images={photos.map(p => ({key: p.key, src: p.image?.[0]?.link}))}
                    visible={isSliderVisible}
                    index={Math.max(openedPhotoIndex, 0)}
                    onIndexChange={index => onOpenedPhotoChange(photos[index]?.key ?? null)}
                    onClose={() => onOpenedPhotoChange(null)}
                />}
        </>
    }